import { gql } from '@apollo/client';
import { Box, Divider, Link, Text, VStack } from '@chakra-ui/react';
import { DocumentNode } from 'graphql';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import { AbbreviateRedraft } from '@/components/AbbreviateRedraft';
import { RawDraftContentStateFragmentAST } from '@/fragments/RawDraftContentStateFragment';
import { usePostHref } from '@/hooks/usePostHref';
import { Card } from '@/imports/ui/chakra/components/Card';

import { Answer_AnswerV2Fragment } from './graphql-types/Answer_AnswerV2Fragment';

import { AnswerContent } from '../AnswerContent';
import { AnswerFooter } from '../AnswerFooter';
import { Title } from '../Title';

const ANSWER_FRAGMENT = gql`
  fragment Answer_AnswerV2Fragment on AnswerV2 {
    _id
    ...AnswerContent_AnswerV2Fragment
    author {
      _id
      displayName
    }
    questionPost {
      _id
      author {
        _id
        displayName
      }
      questionTitle: title
      questionBodyContentState: bodyContentState {
        ...RawDraftContentStateFragment
      }
      answerCount
      ...AnswerFooter_QuestionFragment
      currentUserIsPostAuthor
    }
  }
  ${RawDraftContentStateFragmentAST}
  ${AnswerContent.answerFragment}
  ${AnswerFooter.questionFragment}
`;

type AnswerContentProps = React.ComponentProps<typeof AnswerContent>;

export const Answer: React.FC<{
  answer: Answer_AnswerV2Fragment;
  options?: AnswerContentProps['options'] & { isExpandedByDefault?: boolean };
}> & {
  answerFragment: DocumentNode;
} = ({ answer, options }) => {
  const postHref = usePostHref(answer.questionPost?._id);

  const router = useRouter();

  if (!answer.questionPost) {
    return null;
  }

  return (
    <Box p="4">
      <Box>
        <Link as={NextLink} href={`/members/${answer.author._id}`}>
          {answer.author.displayName}
        </Link>
        {` answered `}
        <Link as={NextLink} href={`/members/${answer.questionPost.author._id}`}>
          {answer.questionPost.author.displayName}
        </Link>
        {`'s question`}
      </Box>
      <Divider pt="4" />
      <VStack pt="2" pb="4" spacing="2" alignItems="start" width="full">
        <NextLink href={postHref}>
          <Title>{answer.questionPost.questionTitle}</Title>
        </NextLink>
        {answer.questionPost.questionBodyContentState && (
          <AbbreviateRedraft
            options={{ defaultIsOpen: options?.isExpandedByDefault }}
            rawDraftContentState={answer.questionPost.questionBodyContentState}
            noOfCharacters={500}
          />
        )}
        <Box>
          <Text as="span" textStyle="compact" color="gray.600">
            Asked by{' '}
          </Text>
          <Text
            as="span"
            fontWeight="semibold"
            textStyle="compact"
            color="gray.600"
          >
            {answer.questionPost.author.displayName}
          </Text>
        </Box>
        <AnswerFooter
          question={answer.questionPost}
          showSeeAllAnswers={answer.questionPost.answerCount > 1}
        />
      </VStack>
      <Card>
        <AnswerContent
          answer={answer}
          options={options}
          onClickComment={() => router.push(postHref)}
          onClickCommentCount={() => router.push(postHref)}
        />
      </Card>
    </Box>
  );
};

Answer.answerFragment = ANSWER_FRAGMENT;
